import React, { useState } from 'react';
import { Box, Button, CircularProgress, Alert, Typography, Paper } from '@mui/material';
import { Email, CheckCircle } from '@mui/icons-material';
import mfaApiService from 'api/MfaApiService';
import './MfaEmailEnable.scss';

interface MfaEmailEnableProps {
  userEmail: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

const MfaEmailEnable: React.FC<MfaEmailEnableProps> = ({ userEmail, onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleEnable = async () => {
    try {
      setLoading(true);
      setError(null);
      await mfaApiService.enableEmailMfa();
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to enable Email OTP MFA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="mfa-email-enable-container">
      <Paper elevation={3} className="mfa-email-enable-paper">
        <Box className="mfa-email-enable-header">
          <Email className="mfa-email-icon" color="secondary" />
          <Typography variant="h5" gutterBottom>
            Enable Email OTP
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" className="mfa-email-alert" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success ? (
          <Alert severity="success" className="mfa-email-alert" icon={<CheckCircle />}>
            Email OTP MFA has been successfully enabled!
          </Alert>
        ) : (
          <>
            <Typography variant="body1" className="mfa-email-description">
              A one-time code will be sent to:
            </Typography>
            <Typography variant="body1" className="mfa-email-address">
              <strong>{userEmail}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mfa-email-note">
              Each time you log in, a 6-digit code will be emailed to you. No app or device setup required — this is
              active immediately after you confirm.
            </Typography>

            <Box className="mfa-email-actions">
              {onCancel && (
                <Button variant="outlined" onClick={onCancel} disabled={loading}>
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                color="secondary"
                onClick={handleEnable}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Email />}
              >
                {loading ? 'Enabling…' : 'Enable Email MFA'}
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MfaEmailEnable;
